<template>
  <Sidebar variant="sidebar" collapsible="icon">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" as-child>
            <router-link to="/">
              <div class="flex justify-center items-center bg-sidebar-primary p-1 rounded-md size-8 aspect-square text-sidebar-primary-foreground">
                <Icon name="tools" size="xl" />
              </div>
              <div class="flex flex-col leading-none">
                <div class="font-['23S'] text-primary text-base truncate leading-none">23Strands</div>
                <div class="font-['23S-Bold'] text-slate-900 text-2xl truncate leading-none">Patient Report Tools</div>
              </div>
            </router-link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupLabel>Tools</SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem v-for="categoryGroup in toolsGroupedByCategory" :key="categoryGroup.category">
            <SidebarMenuButton :tooltip="categoryGroup.displayName" @click="toggleCategory(categoryGroup.category)" :class="{
              'bg-sidebar-accent': expandedCategories[categoryGroup.category],
            }">
              <component :is="categoryGroup.icon" class="size-4" />
              <span>{{ categoryGroup.displayName }}</span>
              <ChevronRight class="ml-auto transition-transform duration-200" :class="{
                'rotate-90': expandedCategories[categoryGroup.category],
              }" />
            </SidebarMenuButton>
            <Transition enter-active-class="transition-all duration-200 ease-out" enter-from-class="opacity-0 max-h-0" enter-to-class="opacity-100 max-h-screen" leave-active-class="transition-all duration-200 ease-out" leave-from-class="opacity-100 max-h-screen" leave-to-class="opacity-0 max-h-0">
              <div v-if="expandedCategories[categoryGroup.category]" class="overflow-hidden">
                <SidebarMenuSub>
                  <SidebarMenuSubItem v-for="tool in categoryGroup.tools" :key="tool.id">
                    <SidebarMenuSubButton :is-active="isActiveRoute(tool.route)" :disabled="tool.status !== 'active'" @click="handleToolClick(tool)" :tooltip="tool.name">
                      <component :is="tool.icon" class="size-4" />
                      <span>{{ tool.name }}</span>
                      <Badge v-if="tool.status === 'in-dev' || tool.badge" :class="[
                        'ml-auto text-black',
                        tool.badge === 'New'
                          ? 'bg-green-300'
                          : tool.badge === 'External'
                          ? 'bg-blue-300'
                          : 'bg-slate-300',
                      ]">
                        {{ tool.badge || "Soon" }}
                      </Badge>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </div>
            </Transition>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarGroupLabel>
            <a target="_blank" :title="gitShortHash || 'Dev'" :href="`https://gitea.23strands.com:3000/23Strands/reports-tools/${(gitHash ? 'commit/' + gitHash : '')}`"> Built {{ gitDate.toLocaleString() }}
            </a>
          </SidebarGroupLabel>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton tooltip="Settings" @click="navigateToSettings">
            <Settings class="size-4" />
            <span>Settings</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { ChevronRight, Settings } from "lucide-vue-next";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { getToolsGroupedByCategory, isToolRouteActive } from "@/config/tools";
import type { Tool } from '@/config/tools'

const gitHash = import.meta.env.VITE_GIT_HASH;
const gitShortHash = import.meta.env.VITE_GIT_SHORT_HASH;
const gitDate = import.meta.env.VITE_GIT_DATE ? new Date(import.meta.env.VITE_GIT_DATE) : new Date();
// const gitUser = import.meta.env.VITE_GIT_USER ?? 'Dev';

const router = useRouter();
const route = useRoute();

const toolsGroupedByCategory = computed(() => getToolsGroupedByCategory());

// Reactive state for expanded categories
const expandedCategories = ref<Record<string, boolean>>({});

const isActiveRoute = (toolRoute?: string): boolean => {
  return isToolRouteActive(route.path, toolRoute);
};

// const _hasActiveToolInCategory = (tools: Tool[]) => {
//   return tools.some(tool => isActiveRoute(tool.route))
// }

const toggleCategory = (categoryId: string) => {
  expandedCategories.value[categoryId] = !expandedCategories.value[categoryId];
};

const navigateToTool = (toolRoute: string) => {
  if (route.path !== toolRoute) {
    router.push(toolRoute);
  }
};

const handleToolClick = (tool: Tool) => {
  if (tool.isExternal && tool.url) {
    window.open(tool.url, '_blank', 'noopener,noreferrer');
  } else if (tool.route) {
    navigateToTool(tool.route);
  }
};

const navigateToSettings = () => {
  if (route.path !== '/settings') {
    router.push('/settings');
  }
};

// Initialize expanded state - expand all categories by default
onMounted(() => {
  toolsGroupedByCategory.value.forEach((categoryGroup) => {
    expandedCategories.value[categoryGroup.category] = true;
  });
});
</script>